import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Sparkles, Loader2, ChevronRight, ChevronLeft, Check, Settings, FileText, Eye, Video as VideoIcon } from 'lucide-react';
import { useAuth } from '@/context/auth';
import { useRestaurantImages, useRestaurantVideos, useVideoJobStatus } from '@/hooks/useRestaurantMedia';
import { VideoModeSelector, VideoMode } from '@/components/media/videos/VideoModeSelector';
import { ModelSelector, SoraModel } from '@/components/media/videos/ModelSelector';
import { VideoConfigForm, VideoConfig } from '@/components/media/videos/VideoConfigForm';
import { VideoJobStatus, VideoJobStatus as VideoJobStatusType } from '@/components/media/videos/VideoJobStatus';
import { VideoPreview } from '@/components/media/videos/VideoPreview';
import { PromptInput } from '@/components/media/shared/PromptInput';
import { MediaSelector } from '@/components/media/shared/MediaSelector';
import { toast } from '@/hooks/use-toast';

type VideoGenerationStep = 'config' | 'prompt' | 'preview';

interface FormStep {
  id: VideoGenerationStep;
  label: string;
  icon: React.ReactNode;
}

const FORM_STEPS: FormStep[] = [
  { id: 'config', label: 'Configuration', icon: <Settings className="h-4 w-4" /> },
  { id: 'prompt', label: 'Prompt', icon: <FileText className="h-4 w-4" /> },
  { id: 'preview', label: 'Preview', icon: <Eye className="h-4 w-4" /> }
];

/**
 * VideosGenerateTab - AI video generation interface with multi-step workflow
 *
 * Step 1 (Configuration):
 * - Generation Mode (Text-to-Video | Image-to-Video | AI-Image-to-Video)
 * - AI Model (Sora 2 | Sora 2 Pro)
 * - Video Config (Size, Duration)
 *
 * Step 2 (Prompt):
 * - Mode-specific prompt inputs
 * - Image selection (for image-to-video mode)
 *
 * Step 3 (Preview):
 * - Job tracking with polling
 * - Video preview when complete
 * - Iterative workflow (Generate Again, Edit)
 *
 * Following Phase 1 pattern from ImagesGenerateTab
 */
export const VideosGenerateTab: React.FC = () => {
  const { user } = useAuth();

  // API hooks
  const { images } = useRestaurantImages(user?.organisationId || '');
  const { generateVideoAsync, isGenerating } = useRestaurantVideos(user?.organisationId || '');

  // Step navigation
  const [currentStep, setCurrentStep] = useState<VideoGenerationStep>('config');
  const [completedSteps, setCompletedSteps] = useState<Set<VideoGenerationStep>>(new Set());

  // Form data (PRESERVED after generation)
  const [mode, setMode] = useState<VideoMode>('text-to-video');
  const [soraModel, setSoraModel] = useState<SoraModel>('sora-2');
  const [config, setConfig] = useState<VideoConfig>({ size: '1280x720', duration: 8 });
  const [prompt, setPrompt] = useState('');
  const [imagePrompt, setImagePrompt] = useState(''); // for AI-image-to-video
  const [selectedImageIds, setSelectedImageIds] = useState<string[]>([]); // for image-to-video
  const [filename, setFilename] = useState(''); // Video filename
  const [tags, setTags] = useState<string>(''); // Comma-separated tags

  // Generation result & job tracking
  const [generatedVideoUrl, setGeneratedVideoUrl] = useState<string | null>(null);
  const [videoJobId, setVideoJobId] = useState<string | null>(null);
  const [generationSuccess, setGenerationSuccess] = useState(false);
  const [generationFailed, setGenerationFailed] = useState(false);

  // Job status polling - poll when we have a job ID
  // Don't disable on success - let refetchInterval handle when to stop
  const { data: jobData } = useVideoJobStatus(
    videoJobId,
    !!videoJobId
  );

  // Derive job status and progress from polled data
  const jobStatus: VideoJobStatusType | null = jobData?.status || null;
  const jobProgress = jobData?.progress || 0;

  // Auto-update video URL and success state when job completes
  useEffect(() => {
    // Check both public_url and video_url (backend returns video_url for job status)
    const videoUrl = jobData?.public_url || jobData?.video_url;

    if (jobData && jobData.status === 'completed' && videoUrl) {
      // Only update if we haven't already set success to avoid duplicate toasts
      if (!generationSuccess) {
        console.log('[VideosGenerateTab] Video completed, setting URL:', videoUrl);
        setGeneratedVideoUrl(videoUrl);
        setGenerationSuccess(true);
        setGenerationFailed(false);
        toast({
          title: 'Video generated!',
          description: 'Your video is ready to watch and download'
        });
      }
    } else if (jobData && jobData.status === 'failed' && !generationFailed) {
      // Set failed state to show "Generate Again" button
      setGenerationFailed(true);
      setGenerationSuccess(false);
      toast({
        title: 'Generation failed',
        description: jobData.error_message || 'Unknown error',
        variant: 'destructive'
      });
    }
  }, [jobData, generationSuccess, generationFailed]);

  // Step validation
  const validateStep = (step: VideoGenerationStep): boolean => {
    if (step === 'config') {
      return true; // All config fields have defaults
    }

    if (step === 'prompt') {
      if (!prompt.trim()) {
        toast({
          title: 'Validation error',
          description: 'Please enter a prompt to generate a video',
          variant: 'destructive'
        });
        return false;
      }

      // Validate image selection for image-to-video mode
      if (mode === 'image-to-video' && selectedImageIds.length === 0) {
        toast({
          title: 'Validation error',
          description: 'Please select an image to animate',
          variant: 'destructive'
        });
        return false;
      }

      // Validate both prompts for AI-image-to-video mode
      if (mode === 'generated-image-to-video' && !imagePrompt.trim()) {
        toast({
          title: 'Validation error',
          description: 'Please enter both image and video prompts',
          variant: 'destructive'
        });
        return false;
      }
    }

    return true;
  };

  // Step navigation handlers
  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCompletedSteps(prev => new Set([...prev, currentStep]));

      const currentIndex = FORM_STEPS.findIndex(s => s.id === currentStep);
      if (currentIndex < FORM_STEPS.length - 1) {
        setCurrentStep(FORM_STEPS[currentIndex + 1].id);
      }
    }
  };

  const handlePrevious = () => {
    const currentIndex = FORM_STEPS.findIndex(s => s.id === currentStep);
    if (currentIndex > 0) {
      setCurrentStep(FORM_STEPS[currentIndex - 1].id);
    }
  };

  const handleStepClick = (stepId: VideoGenerationStep) => {
    const stepIndex = FORM_STEPS.findIndex(s => s.id === stepId);
    const currentIndex = FORM_STEPS.findIndex(s => s.id === currentStep);

    if (stepId === currentStep || completedSteps.has(stepId)) {
      setCurrentStep(stepId);
      return;
    }

    if (stepIndex === currentIndex + 1) {
      const allPreviousCompleted = FORM_STEPS
        .slice(0, stepIndex)
        .every(step => completedSteps.has(step.id));

      if (allPreviousCompleted) {
        handleNext();
      }
    }
  };

  const handleGenerate = async () => {
    if (!validateStep('prompt')) {
      return;
    }

    if (!user?.organisationId) {
      toast({
        title: 'Error',
        description: 'User organization not found',
        variant: 'destructive'
      });
      return;
    }

    try {
      // Prepare video generation request
      const requestData: any = {
        mode,
        prompt,
        organisation_id: user.organisationId,
        soraModel,
        videoConfig: {
          size: config.size,
          seconds: config.duration
        }
      };

      // Add mode-specific data
      if (mode === 'generated-image-to-video') {
        requestData.imagePrompt = imagePrompt;
      } else if (mode === 'image-to-video' && selectedImageIds.length > 0) {
        // Look up the selected image to get its actual source_type
        const selectedImage = images.find(img => img.id === selectedImageIds[0]);
        if (!selectedImage) {
          throw new Error('Selected image not found');
        }

        // Map source_type to backend format
        const sourceType = selectedImage.source_type === 'ai_generated' ? 'ai' : 'uploaded';

        requestData.inputSource = {
          imageId: selectedImageIds[0],
          sourceType: sourceType
        };
      }

      // Add filename if provided
      if (filename.trim()) {
        requestData.filename = filename.trim();
      }

      // Parse and add tags if provided
      if (tags.trim()) {
        requestData.tags = tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
      }

      console.log('Generating video with:', requestData);

      // Call API to generate video
      const job = await generateVideoAsync(requestData);

      // Set job ID to start polling and reset states
      setVideoJobId(job.id);
      setGenerationSuccess(false);
      setGenerationFailed(false);
      setGeneratedVideoUrl(null);

      console.log('Video generation job created:', job.id);

    } catch (error: any) {
      console.error('Video generation failed:', error);
      setGenerationSuccess(false);
      setGenerationFailed(true);
      setGeneratedVideoUrl(null);
      // Error toast is already shown by the mutation's onError
    }
  };

  const handleStartNew = () => {
    setPrompt('');
    setImagePrompt('');
    setSelectedImageIds([]);
    setFilename('');
    setTags('');
    setCurrentStep('config');
    setCompletedSteps(new Set());
    setGeneratedVideoUrl(null);
    setGenerationSuccess(false);
    setGenerationFailed(false);
    setVideoJobId(null);
  };

  const getStepStatus = (stepId: VideoGenerationStep): 'completed' | 'current' | 'upcoming' => {
    if (completedSteps.has(stepId)) return 'completed';
    if (stepId === currentStep) return 'current';
    return 'upcoming';
  };

  const isStepClickable = (stepId: VideoGenerationStep): boolean => {
    if (stepId === currentStep) return true;
    if (completedSteps.has(stepId)) return true;

    const stepIndex = FORM_STEPS.findIndex(s => s.id === stepId);
    if (stepIndex > 0) {
      return FORM_STEPS.slice(0, stepIndex).every(step => completedSteps.has(step.id));
    }

    return false;
  };

  return (
    <div className="space-y-6">
      {/* Step Progress Indicator - Copied from ImagesGenerateTab */}
      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center justify-center gap-2 sm:gap-3 overflow-x-auto">
            {FORM_STEPS.map((step, index) => {
              const status = getStepStatus(step.id);
              const clickable = isStepClickable(step.id);

              return (
                <div key={step.id} className="flex items-center">
                  <button
                    onClick={() => clickable && handleStepClick(step.id)}
                    disabled={!clickable}
                    className={`
                      flex items-center gap-2 px-4 sm:px-6 py-2.5 rounded-lg transition-all font-medium
                      ${status === 'completed' ? 'bg-green-600 hover:bg-green-700' : ''}
                      ${status === 'current' ? 'bg-primary hover:bg-primary/90' : ''}
                      ${status === 'upcoming' ? 'bg-muted' : ''}
                      ${clickable ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}
                    `}
                  >
                    <div className="flex items-center gap-2">
                      {status === 'completed' ? (
                        <Check className="h-4 w-4 text-white stroke-white" />
                      ) : (
                        <span className={`${status === 'current' ? 'text-white [&_svg]:stroke-white' : 'text-muted-foreground'}`}>
                          {step.icon}
                        </span>
                      )}
                      <span className={`text-sm whitespace-nowrap hidden sm:inline ${
                        status === 'completed' || status === 'current' ? 'text-white' : 'text-muted-foreground'
                      }`}>
                        {step.label}
                      </span>
                    </div>
                  </button>

                  {index < FORM_STEPS.length - 1 && (
                    <div
                      className={`w-8 sm:w-16 h-0.5 ${
                        completedSteps.has(FORM_STEPS[index + 1].id)
                          ? 'bg-green-600'
                          : 'bg-border'
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Step 1: Configuration */}
      {currentStep === 'config' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <VideoIcon className="h-5 w-5 text-primary" />
                  <CardTitle>Configure Your Video</CardTitle>
                </div>
                <CardDescription>
                  Choose how you want to generate your video
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <VideoModeSelector value={mode} onChange={setMode} />
                <Separator />
                <ModelSelector value={soraModel} onChange={setSoraModel} />
                <Separator />
                <VideoConfigForm config={config} onChange={setConfig} />

                <div className="flex justify-end pt-4">
                  <Button onClick={handleNext} size="lg">
                    Next: Enter Prompt
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Preview Column */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Video Specifications</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div>
                  <Label className="text-xs text-muted-foreground">Mode</Label>
                  <p className="font-medium">
                    {mode === 'text-to-video' && 'Text-to-Video'}
                    {mode === 'image-to-video' && 'Image-to-Video'}
                    {mode === 'generated-image-to-video' && 'AI Image-to-Video'}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Model</Label>
                  <p className="font-medium">{soraModel === 'sora-2' ? 'Sora 2' : 'Sora 2 Pro'}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Resolution</Label>
                  <p className="font-medium">{config.size}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Duration</Label>
                  <p className="font-medium">{config.duration} seconds</p>
                </div>
                <Separator />
                <div>
                  <Label className="text-xs text-muted-foreground">Estimated Time</Label>
                  <p className="font-medium">
                    {soraModel === 'sora-2' ? '~1 minute' : '~2 minutes'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Step 2: Prompt */}
      {currentStep === 'prompt' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Configuration Summary */}
            <Card className="bg-muted/50">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="text-sm font-medium">Selected Configuration:</span>
                  <Badge variant="outline">
                    {mode === 'text-to-video' && 'Text-to-Video'}
                    {mode === 'image-to-video' && 'Image-to-Video'}
                    {mode === 'generated-image-to-video' && 'AI Image-to-Video'}
                  </Badge>
                  <Badge variant="outline">{soraModel === 'sora-2' ? 'Sora 2' : 'Sora 2 Pro'}</Badge>
                  <Badge variant="secondary">{config.size} • {config.duration}s</Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentStep('config')}
                    className="ml-auto"
                  >
                    Change Configuration
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Enter Your Prompt</CardTitle>
                <CardDescription>
                  {mode === 'text-to-video' && 'Describe the video you want to generate'}
                  {mode === 'image-to-video' && 'Select an image and describe how to animate it'}
                  {mode === 'generated-image-to-video' && 'Describe the image to generate, then how to animate it'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Text-to-Video Mode */}
                {mode === 'text-to-video' && (
                  <>
                    <PromptInput
                      label="Video Prompt"
                      value={prompt}
                      onChange={setPrompt}
                      placeholder="A cinematic shot of a gourmet burger being assembled, with melted cheese slowly dripping, professional food videography, 4K quality, warm lighting"
                      helperText="Describe the video scene, camera movements, and visual style"
                      required
                      rows={8}
                      maxLength={500}
                      disabled={isGenerating}
                    />

                    <Separator />

                    {/* Filename and Tags Inputs */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="filename-text">
                          Video Filename <span className="text-muted-foreground">(optional)</span>
                        </Label>
                        <Input
                          id="filename-text"
                          value={filename}
                          onChange={(e) => setFilename(e.target.value)}
                          placeholder="burger-assembly-cinematic"
                          disabled={isGenerating}
                        />
                        <p className="text-xs text-muted-foreground">
                          A descriptive name for your video
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="tags-text">
                          Tags <span className="text-muted-foreground">(optional)</span>
                        </Label>
                        <Input
                          id="tags-text"
                          value={tags}
                          onChange={(e) => setTags(e.target.value)}
                          placeholder="burger, cinematic, promo"
                          disabled={isGenerating}
                        />
                        <p className="text-xs text-muted-foreground">
                          Comma-separated tags for organizing your videos
                        </p>
                      </div>
                    </div>
                  </>
                )}

                {/* Image-to-Video Mode */}
                {mode === 'image-to-video' && (
                  <div className="space-y-6">
                    <MediaSelector
                      value={selectedImageIds}
                      onChange={setSelectedImageIds}
                      min={1}
                      max={1}
                      showRefresh={true}
                    />

                    <PromptInput
                      label="Animation Instructions"
                      value={prompt}
                      onChange={setPrompt}
                      placeholder="Slowly zoom in on the burger while steam rises from the fresh-cooked patty. Add subtle camera movement for cinematic effect."
                      helperText="Describe how the image should move and animate"
                      required
                      rows={6}
                      maxLength={500}
                      disabled={isGenerating}
                    />

                    <Separator />

                    {/* Filename and Tags Inputs */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="filename-img">
                          Video Filename <span className="text-muted-foreground">(optional)</span>
                        </Label>
                        <Input
                          id="filename-img"
                          value={filename}
                          onChange={(e) => setFilename(e.target.value)}
                          placeholder="burger-zoom-animation"
                          disabled={isGenerating}
                        />
                        <p className="text-xs text-muted-foreground">
                          A descriptive name for your video
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="tags-img">
                          Tags <span className="text-muted-foreground">(optional)</span>
                        </Label>
                        <Input
                          id="tags-img"
                          value={tags}
                          onChange={(e) => setTags(e.target.value)}
                          placeholder="animation, burger, zoom"
                          disabled={isGenerating}
                        />
                        <p className="text-xs text-muted-foreground">
                          Comma-separated tags for organizing your videos
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* AI-Image-to-Video Mode */}
                {mode === 'generated-image-to-video' && (
                  <div className="space-y-6">
                    <PromptInput
                      label="Image Generation Prompt"
                      value={imagePrompt}
                      onChange={setImagePrompt}
                      placeholder="Professional food photography of a gourmet burger with melted cheese, fresh lettuce, and caramelized onions on a rustic wooden board"
                      helperText="First, describe the image to generate"
                      required
                      rows={4}
                      maxLength={500}
                      disabled={isGenerating}
                    />

                    <PromptInput
                      label="Animation Instructions"
                      value={prompt}
                      onChange={setPrompt}
                      placeholder="Slowly rotate around the burger while steam rises. Add a subtle zoom in to highlight the layers."
                      helperText="Then, describe how to animate that image"
                      required
                      rows={4}
                      maxLength={500}
                      disabled={isGenerating}
                    />

                    <Separator />

                    {/* Filename and Tags Inputs */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="filename-ai">
                          Video Filename <span className="text-muted-foreground">(optional)</span>
                        </Label>
                        <Input
                          id="filename-ai"
                          value={filename}
                          onChange={(e) => setFilename(e.target.value)}
                          placeholder="burger-rotate-360"
                          disabled={isGenerating}
                        />
                        <p className="text-xs text-muted-foreground">
                          A descriptive name for your video
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="tags-ai">
                          Tags <span className="text-muted-foreground">(optional)</span>
                        </Label>
                        <Input
                          id="tags-ai"
                          value={tags}
                          onChange={(e) => setTags(e.target.value)}
                          placeholder="ai-generated, rotation, burger"
                          disabled={isGenerating}
                        />
                        <p className="text-xs text-muted-foreground">
                          Comma-separated tags for organizing your videos
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between pt-4">
                  <Button variant="outline" onClick={handlePrevious}>
                    <ChevronLeft className="mr-2 h-4 w-4" />
                    Previous
                  </Button>

                  <Button
                    onClick={handleNext}
                    disabled={
                      !prompt.trim() ||
                      (mode === 'image-to-video' && selectedImageIds.length === 0) ||
                      (mode === 'generated-image-to-video' && !imagePrompt.trim())
                    }
                    size="lg"
                  >
                    Preview & Generate
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tips Column */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Tips for Better Videos</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <h4 className="font-semibold text-sm mb-1">Example Prompts:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                    <li>Cinematic food reveal with steam rising</li>
                    <li>Slow-motion pour of sauce over dish</li>
                    <li>360-degree rotation of plated meal</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-sm mb-1">Best Practices:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                    <li>Describe camera movements clearly</li>
                    <li>Mention lighting and atmosphere</li>
                    <li>Specify professional/cinematic style</li>
                    <li>Keep motions simple and smooth</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-sm mb-1">Generation Info:</h4>
                  <p className="text-sm text-muted-foreground">
                    • OpenAI Sora {soraModel === 'sora-2' ? '2' : '2 Pro'}<br />
                    • Time: {soraModel === 'sora-2' ? '~1 min' : '~2 min'}<br />
                    • Cost: ~${soraModel === 'sora-2' ? '0.50' : '1.00'} per video
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Step 3: Preview & Generate with Job Tracking */}
      {currentStep === 'preview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Configuration Summary */}
            <Card>
              <CardHeader>
                <CardTitle>
                  {generationSuccess ? 'Your Generated Video' : generationFailed ? 'Generation Failed' : 'Review Your Configuration'}
                </CardTitle>
                <CardDescription>
                  {generationSuccess
                    ? 'Video generated successfully - watch, download, or create another'
                    : generationFailed
                    ? 'The video generation failed - adjust your settings and try again'
                    : jobStatus
                    ? 'Video is being generated - this may take 1-2 minutes'
                    : 'Check everything before generating your video'
                  }
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Show video player if generation complete */}
                {generatedVideoUrl && (
                  <div className="space-y-4">
                    <VideoPreview videoUrl={generatedVideoUrl} />
                    <Separator />
                  </div>
                )}

                {/* Show job status if generating */}
                {jobStatus && !generationSuccess && (
                  <div className="space-y-4">
                    <VideoJobStatus
                      status={jobStatus}
                      progress={jobProgress}
                      showProgress={true}
                    />
                    <Separator />
                  </div>
                )}

                {/* Configuration details */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Mode</Label>
                    <p className="font-medium">
                      {mode === 'text-to-video' && 'Text-to-Video'}
                      {mode === 'image-to-video' && 'Image-to-Video'}
                      {mode === 'generated-image-to-video' && 'AI Image-to-Video'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Model</Label>
                    <p className="font-medium">{soraModel === 'sora-2' ? 'Sora 2' : 'Sora 2 Pro'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Size</Label>
                    <p className="font-medium">{config.size}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Duration</Label>
                    <p className="font-medium">{config.duration}s</p>
                  </div>
                </div>

                <Separator />

                <div>
                  <Label className="text-xs text-muted-foreground">Your Prompt</Label>
                  <Card className="mt-2 bg-muted/50">
                    <CardContent className="p-4">
                      <p className="text-sm">{prompt}</p>
                      {mode === 'generated-image-to-video' && imagePrompt && (
                        <div className="mt-2 pt-2 border-t">
                          <p className="text-xs text-muted-foreground mb-1">Image Prompt:</p>
                          <p className="text-sm">{imagePrompt}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  <div className="flex gap-2 mt-2">
                    <Button variant="ghost" size="sm" onClick={() => setCurrentStep('config')}>
                      Edit Configuration
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setCurrentStep('prompt')}>
                      Edit Prompt
                    </Button>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex items-center justify-between pt-4">
                  {!jobStatus && !generationSuccess && !generationFailed ? (
                    <>
                      <Button variant="outline" onClick={handlePrevious}>
                        <ChevronLeft className="mr-2 h-4 w-4" />
                        Previous
                      </Button>
                      <Button onClick={handleGenerate} disabled={isGenerating} size="lg">
                        {isGenerating ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            Starting Generation...
                          </>
                        ) : (
                          <>
                            <Sparkles className="mr-2 h-5 w-5" />
                            Generate Video
                          </>
                        )}
                      </Button>
                    </>
                  ) : generationSuccess || generationFailed ? (
                    <>
                      <Button variant="outline" onClick={handleStartNew}>
                        Start New Generation
                      </Button>
                      <Button onClick={handleGenerate} disabled={isGenerating} size="lg">
                        {isGenerating ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="mr-2 h-5 w-5" />
                            {generationFailed ? 'Try Again' : 'Generate Again'}
                          </>
                        )}
                      </Button>
                    </>
                  ) : (
                    <div className="flex-1 text-center">
                      <p className="text-sm text-muted-foreground">
                        Generating video... This may take {soraModel === 'sora-2' ? '~1 minute' : '~2 minutes'}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Info Column */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {generationSuccess ? 'What Now?' : generationFailed ? 'Try Again' : jobStatus ? 'Generating...' : 'What Happens Next?'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {generationSuccess ? (
                  <>
                    <p className="text-sm text-muted-foreground">
                      Your video has been saved to the Library tab. You can:
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-2 list-disc list-inside">
                      <li>Download it using the button above</li>
                      <li>Find it in the Library tab for use in campaigns</li>
                      <li>Edit your configuration and generate again</li>
                      <li>Start a completely new generation</li>
                    </ul>
                  </>
                ) : generationFailed ? (
                  <>
                    <p className="text-sm text-muted-foreground">
                      The generation failed. You can:
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-2 list-disc list-inside">
                      <li>Adjust your prompt to avoid content restrictions</li>
                      <li>Try a different video mode or model</li>
                      <li>Click "Try Again" to retry with current settings</li>
                      <li>Click "Edit Prompt" or "Edit Configuration" to make changes</li>
                    </ul>
                    <div className="pt-2">
                      <p className="text-xs text-muted-foreground">
                        💡 <strong>Tip:</strong> If blocked by moderation, try avoiding specific people, ages, or brand names.
                      </p>
                    </div>
                  </>
                ) : jobStatus ? (
                  <>
                    <p className="text-sm text-muted-foreground">
                      Your video is being generated:
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-2 list-disc list-inside">
                      <li>Status updates every 10 seconds</li>
                      <li>Estimated time: {soraModel === 'sora-2' ? '~1 minute' : '~2 minutes'}</li>
                      <li>The video will appear here when ready</li>
                      <li>You can leave this page and come back</li>
                    </ul>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">
                      When you click "Generate Video":
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-2 list-disc list-inside">
                      <li>Your video will be generated using OpenAI Sora</li>
                      <li>This typically takes 1-2 minutes</li>
                      <li>The preview will appear here automatically</li>
                      <li>You can then download or generate again</li>
                    </ul>
                    <div className="pt-2">
                      <p className="text-xs text-muted-foreground">
                        💡 <strong>Tip:</strong> After generation, you can make adjustments and regenerate without starting over.
                      </p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideosGenerateTab;
